import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { errorText } from "@common/api";
import { admin, queryKeys } from "../../api/salvagers";

/** A cell: strings as they are, anything else as JSON. */
export const cellText = (value: unknown): string =>
  value === undefined || value === null ? "" : typeof value === "string" ? value : JSON.stringify(value);

/** The columns a set of rows needs: every key seen, sorted. */
export const columnsOf = (rows: Record<string, unknown>[]): string[] =>
  [...new Set(rows.flatMap((row) => Object.keys(row)))].sort();

function TableView({ tableKey }: { tableKey: string }) {
  const rows = useQuery({ queryKey: queryKeys.table(tableKey), queryFn: () => admin.table(tableKey) });
  if (rows.error) return <p className="notice error">{errorText(rows.error, "Could not read the table.")}</p>;
  if (!rows.data) return <p className="muted small">Loading…</p>;
  if (rows.data.items.length === 0) return <p className="muted small">Empty.</p>;
  const columns = columnsOf(rows.data.items);
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column}>{column}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.data.items.map((item, index) => (
            <tr key={index}>
              {columns.map((column) => (
                <td key={column}>{cellText(item[column])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Everything the site stores, straight from DynamoDB, one table at a time. */
export default function Tables() {
  const tables = useQuery({ queryKey: queryKeys.tables, queryFn: admin.tables });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <section>
      <h2>Tables</h2>
      <p className="muted">Everything the site stores, straight from DynamoDB.</p>
      {tables.error && <p className="notice error">{errorText(tables.error, "Could not list the tables.")}</p>}
      <ul className="list">
        {tables.data?.map((table) => (
          <li key={table.key}>
            <span>
              <strong>{table.key}</strong> <span className="muted small">{table.name}</span>
            </span>
            <button className="btn" onClick={() => setOpen(open === table.key ? null : table.key)}>
              {open === table.key ? "Hide" : "View"}
            </button>
          </li>
        ))}
      </ul>
      {open && <TableView tableKey={open} />}
    </section>
  );
}
